import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronRight, MapPin, Eye, EyeOff, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail, inr } from "@/lib/api";
import { fmt12 } from "@/lib/time";
import { useAuth } from "@/contexts/AuthContext";

const timeSlots = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];

export default function Book() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();

  const initType = params.get("type") === "doorstep" ? "doorstep" : "spa";
  const [state, setState] = useState({
    booking_type: initType,
    branch_id: params.get("branch") || "",
    date: new Date().toISOString().slice(0, 10),
    time: "",
    service_id: "",
    package_id: params.get("package") || "",
    therapist_id: params.get("therapist") || "",
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || "",
    password: "",
    location: "",
    notes: "",
  });

  const [step, setStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [, setBookingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [therapistsLoading, setTherapistsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentMode, setPaymentMode] = useState("pay_at_counter");

  useEffect(() => {
    if (user) {
      api.get("/wallet/mine")
        .then((r) => setWalletBalance(r.data?.balance || 0))
        .catch(() => {});
    }
  }, [user]);

  const validateEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || val.trim() === "") {
      return "Email address is required";
    } else if (!emailRegex.test(val.trim())) {
      return "Please enter a valid email address (e.g. name@domain.com)";
    }
    return "";
  };

  const handleEmailChange = (val) => {
    setState((prev) => ({ ...prev, customer_email: val }));
    if (emailTouched) {
      setEmailError(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(state.customer_email));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/branches"),
      api.get("/services"),
      api.get("/packages")
    ])
      .then(([bRes, sRes, pRes]) => {
        setBranches(bRes.data);
        setServices(sRes.data);
        setPackages(pRes.data);
      })
      .catch(() => {
        toast.error("Failed to load booking details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (state.booking_type === "doorstep") {
      setTherapistsLoading(true);
      api.get("/therapists")
        .then((r) => setTherapists(r.data))
        .catch(() => {})
        .finally(() => setTherapistsLoading(false));
    } else if (state.branch_id) {
      setTherapistsLoading(true);
      api.get(`/therapists?branch_id=${state.branch_id}`)
        .then((r) => setTherapists(r.data))
        .catch(() => {})
        .finally(() => setTherapistsLoading(false));
    }
  }, [state.branch_id, state.booking_type]);

  const selectedPkg = packages.find((p) => p.id === state.package_id);
  const selectedSvc = services.find((s) => s.id === state.service_id);
  const amount = selectedPkg
    ? (state.booking_type === "doorstep" ? selectedPkg.home_price : selectedPkg.spa_price)
    : selectedSvc?.price || 0;

  const steps = useMemo(() => {
    return state.booking_type === "doorstep"
      ? ["Date & Time", "Therapist", "Details", "Confirm Request"]
      : ["Branch", "Date & Time", "Service", "Therapist", "Details", "Payment"];
  }, [state.booking_type]);

  const activeStepName = steps[step];

  const canNext = useMemo(() => {
    const stepName = steps[step];
    switch (stepName) {
      case "Branch":
        return !!state.branch_id;
      case "Date & Time":
        return !!state.date && !!state.time;
      case "Service":
        return !!state.service_id || !!state.package_id;
      case "Therapist":
        return true;
      case "Details":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validBasic = !!state.customer_name && 
               !!state.customer_email && 
               emailRegex.test(state.customer_email) && 
               !!state.customer_phone && 
               (state.booking_type !== "doorstep" || !!state.location);
        if (!user) {
          return validBasic && !!state.password && state.password.length >= 6;
        }
        return validBasic;
      default:
        return false;
    }
  }, [step, state, steps, user]);

  const submit = async () => {
    const err = validateEmail(state.customer_email);
    setEmailTouched(true);
    if (err) {
      setEmailError(err);
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      if (!user && state.password) {
        try {
          await register({
            name: state.customer_name,
            email: state.customer_email,
            phone: state.customer_phone,
            password: state.password,
          });
        } catch (regErr) {
          toast.error(regErr.response?.data?.detail || "Account creation failed. Please try logging in.");
          setBusy(false);
          return;
        }
      }

      const payload = { ...state, payment_mode: paymentMode };
      delete payload.password;
      if (payload.service_id === "") delete payload.service_id;
      if (payload.package_id === "") delete payload.package_id;
      if (payload.therapist_id === "") delete payload.therapist_id;
      const { data } = await api.post("/bookings", payload);
      setBookingId(data.id);
      try {
        await api.post(`/bookings/${data.id}/pay`);
      } catch {}
      toast.success(paymentMode === "wallet" ? "Paid with Wallet & Booking Confirmed!" : "🎉 Account Created & Booking Confirmed!");
      navigate(`/book/success/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const branchBorders = [
    "border-2 border-red-500/80 shadow-md shadow-red-950/30",
    "border-2 border-amber-400/80 shadow-md shadow-amber-950/30",
    "border-2 border-emerald-400/80 shadow-md shadow-emerald-950/30"
  ];

  return (
    <div data-testid="book-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
      <motion.div
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
        className="mb-8"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-2">RESERVE YOUR EXPERIENCE</div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold mb-6">
          Book Your Session
        </h1>

        {/* Booking Type selection Pill Tabs */}
        <div className="inline-flex gap-2 p-1.5 rounded-2xl bg-black border border-white/20 shadow-xl">
          <button 
            onClick={() => {
              setStep(0);
              setState({ ...state, booking_type: "spa", branch_id: "", therapist_id: "" });
            }} 
            data-testid="book-type-spa" 
            className={`px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider font-extrabold transition-all relative z-10 ${
              state.booking_type === "spa"
                ? "bg-gradient-to-r from-amber-400 via-hgold to-yellow-400 text-black shadow-lg shadow-amber-950/50 font-extrabold"
                : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            In-Spa Session
          </button>
          <button 
            onClick={() => {
              setStep(0);
              setState({ ...state, booking_type: "doorstep", branch_id: "", therapist_id: "" });
            }} 
            data-testid="book-type-doorstep" 
            className={`px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider font-extrabold transition-all relative z-10 ${
              state.booking_type === "doorstep"
                ? "bg-gradient-to-r from-emerald-400 to-teal-400 text-black shadow-lg shadow-emerald-950/50 font-extrabold"
                : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            Doorstep Service
          </button>
        </div>
      </motion.div>

      {/* Steps indicator bar */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
        className={`rounded-2xl border p-4 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-lg mb-8 transition-colors ${
          state.booking_type === "doorstep" ? "border-emerald-400/40" : "border-amber-400/40"
        }`}
      >
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {steps.map((s, i) => {
            const isDoorstep = state.booking_type === "doorstep";
            const isCompleted = i < step;
            const isActive = i === step;

            return (
              <div key={s} className="flex items-center gap-2 shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  isCompleted
                    ? isDoorstep ? "bg-emerald-400 text-black font-extrabold" : "bg-amber-400 text-black font-extrabold"
                    : isActive
                    ? isDoorstep
                      ? "bg-gradient-to-r from-emerald-400 to-teal-400 text-black font-extrabold shadow-md shadow-emerald-950/50 scale-105"
                      : "bg-gradient-to-r from-amber-400 to-yellow-400 text-black font-extrabold shadow-md shadow-amber-950/50 scale-105"
                    : "border border-white/20 text-white/40 bg-black/40"
                }`}>
                  {isCompleted ? <Check size={14}/> : i + 1}
                </div>
                <div className={`text-xs uppercase tracking-wider font-bold ${
                  isActive
                    ? isDoorstep ? "text-emerald-400" : "text-amber-400"
                    : isCompleted
                    ? isDoorstep ? "text-emerald-300/80" : "text-amber-300/80"
                    : "text-white/40"
                }`}>{s}</div>
                {i < steps.length - 1 && <ChevronRight className="text-white/20" size={14} />}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Main Step Content Box */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
        className="p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper border-2 border-hgold/70 shadow-xl shadow-yellow-950/30 min-h-[420px] flex flex-col justify-between"
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="premium-spinner mb-4"></div>
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Loading booking options...</div>
          </div>
        ) : (
          <div className="w-full">
            {/* STEP: Branch */}
            {activeStepName === "Branch" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Select a Branch</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {branches.map((b, idx) => {
                    const borderStyle = branchBorders[idx % branchBorders.length];
                    const isSelected = state.branch_id === b.id;
                    return (
                      <button
                        key={b.id}
                        onClick={() => setState({ ...state, branch_id: b.id })}
                        data-testid={`book-branch-${b.slug}`}
                        className={`rounded-2xl p-6 text-left transition-all ${
                          isSelected
                            ? `${borderStyle} bg-amber-950/30 ring-2 ring-amber-400/50 scale-[1.02]`
                            : `${borderStyle} bg-black/60 hover:scale-[1.01]`
                        }`}
                      >
                        <div className="font-heading text-xl text-white font-bold mb-2">{b.name}</div>
                        <div className="text-white/80 text-xs flex items-center gap-1 font-medium"><MapPin size={13} className="text-amber-400"/>{b.city}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP: Date & Time */}
            {activeStepName === "Date & Time" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Choose Date & Time Slot</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-2">Preferred Date</label>
                    <input 
                      type="date" 
                      data-testid="book-date" 
                      min={new Date().toISOString().slice(0, 10)} 
                      value={state.date} 
                      onChange={(e) => setState({ ...state, date: e.target.value })} 
                      className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-white outline-none rounded-xl date-picker-custom text-sm font-medium" 
                    />
                    <style>{`
                      .date-picker-custom::-webkit-calendar-picker-indicator {
                        filter: invert(53%) sepia(87%) saturate(357%) hue-rotate(5deg) brightness(91%) contrast(89%);
                        cursor: pointer;
                        font-size: 1.25rem;
                      }
                    `}</style>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-2">Time Slot</label>
                    <div className="grid grid-cols-3 gap-2.5">
                      {timeSlots.map((t) => {
                        const isSelected = state.time === t;
                        return (
                          <button 
                            key={t} 
                            type="button"
                            onClick={() => setState({ ...state, time: t })}
                            className={`py-2.5 px-2 text-xs font-extrabold rounded-xl border transition-all ${
                              isSelected
                                ? "border-2 border-amber-400 bg-amber-500/20 text-amber-300 shadow-md shadow-amber-950/40"
                                : "border-white/15 text-white/70 hover:border-amber-400/50 bg-black/50"
                            }`}
                          >
                            {fmt12(t)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP: Service */}
            {activeStepName === "Service" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Select Treatment</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {services.map((s, idx) => {
                    const isSelected = state.service_id === s.id;
                    const borderStyle = branchBorders[idx % branchBorders.length];
                    return (
                      <button
                        key={s.id}
                        onClick={() => setState({ ...state, service_id: s.id, package_id: "" })}
                        className={`rounded-2xl p-5 text-left transition-all ${
                          isSelected
                            ? `${borderStyle} bg-amber-950/40 ring-2 ring-amber-400/50 scale-[1.02]`
                            : `${borderStyle} bg-black/60 hover:scale-[1.01]`
                        }`}
                      >
                        <div className="font-heading text-lg text-white font-bold mb-1">{s.name}</div>
                        <div className="text-[11px] text-emerald-400 uppercase tracking-widest font-extrabold mb-3">{s.duration_min} mins session</div>
                        <div className="text-amber-400 text-lg font-bold">{inr(s.price)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP: Therapist Choice */}
            {activeStepName === "Therapist" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Select a Therapist</h2>
                {therapistsLoading ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <div className="premium-spinner mb-3"></div>
                    <div className="text-xs text-emerald-400 uppercase tracking-widest font-bold">Loading therapists...</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <button
                      onClick={() => setState({ ...state, therapist_id: "" })}
                      className={`rounded-2xl border-2 p-5 text-left transition-all flex items-center gap-4 min-h-[100px] ${
                        state.therapist_id === ""
                          ? "border-amber-400 bg-amber-950/30 ring-2 ring-amber-400/50"
                          : "border-white/20 bg-black/60 hover:border-amber-400/50"
                      }`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shrink-0 font-bold text-black text-xs">
                        ANY
                      </div>
                      <div>
                        <div className="font-heading text-lg text-white font-bold">No Preference</div>
                        <div className="text-xs text-white/70 mt-1 font-normal">Assign any available therapist</div>
                      </div>
                    </button>

                    {therapists.map((t, idx) => {
                      const isSelected = state.therapist_id === t.id;
                      const borderStyle = branchBorders[idx % branchBorders.length];
                      return (
                        <button
                          key={t.id}
                          onClick={() => setState({ ...state, therapist_id: t.id })}
                          className={`rounded-2xl p-5 text-left transition-all flex items-center gap-4 min-h-[100px] ${
                            isSelected
                              ? `${borderStyle} bg-amber-950/30 ring-2 ring-amber-400/50`
                              : `${borderStyle} bg-black/60 hover:scale-[1.01]`
                          }`}
                        >
                          <img 
                            src={t.photos?.[0] || t.avatar_url || "/logo.png"} 
                            alt={t.display_name} 
                            className="w-14 h-14 rounded-xl object-cover border-2 border-amber-400/50 shrink-0" 
                          />
                          <div>
                            <div className="font-heading text-lg text-white font-bold">{t.display_name}</div>
                            <div className="text-xs text-emerald-400 mt-1 uppercase tracking-widest font-semibold">{t.skills?.slice(0,2).join(", ")}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* STEP: Details */}
            {activeStepName === "Details" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Customer Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-1.5">Full Name</label>
                    <input
                      placeholder="Enter your full name"
                      data-testid="book-name"
                      value={state.customer_name}
                      onChange={(e) => setState({ ...state, customer_name: e.target.value })}
                      className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-white/90 block mb-1.5">Email Address</label>
                      <input
                        placeholder="Enter email address"
                        type="email"
                        data-testid="book-email"
                        value={state.customer_email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        onBlur={handleEmailBlur}
                        className={`w-full bg-black/70 border ${
                          emailError ? "border-red-500 focus:border-red-500" : "border-white/20 focus:border-emerald-400"
                        } px-4 py-3 text-sm text-white outline-none rounded-xl transition-all`}
                      />
                      {emailError && (
                        <span className="text-red-400 text-xs font-semibold mt-1.5 block animate-pulse">
                          ⚠️ {emailError}
                        </span>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-white/90 block mb-1.5">Phone Number</label>
                      <input
                        placeholder="Enter phone number"
                        data-testid="book-phone"
                        value={state.customer_phone}
                        onChange={(e) => setState({ ...state, customer_phone: e.target.value })}
                        className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                      />
                    </div>
                  </div>

                  {!user && (
                    <div className="p-4 rounded-xl border border-amber-400/60 bg-amber-950/20 space-y-3">
                      <div className="text-xs font-extrabold uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
                        <UserPlus size={14} className="text-amber-400" /> Account Creation (New Guest)
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-white/80 block mb-1">Create Account Password *</label>
                        <div className="relative flex items-center">
                          <input
                            type={showPassword ? "text" : "password"}
                            required
                            minLength={6}
                            placeholder="Minimum 6 characters"
                            value={state.password || ""}
                            onChange={(e) => setState({ ...state, password: e.target.value })}
                            className="w-full bg-black/80 border border-white/20 focus:border-amber-400 px-4 py-2.5 pr-10 text-sm text-white outline-none rounded-xl"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 text-white/60 hover:text-amber-400 p-1"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <span className="text-[10px] text-amber-300/80 mt-1 block">
                          🔒 An account will be created automatically so you can track your appointment & wallet credits.
                        </span>
                      </div>
                    </div>
                  )}

                  {state.booking_type === "doorstep" && (
                    <div>
                      <label className="text-sm font-semibold text-white/90 block mb-1.5">Doorstep Address & Landmark</label>
                      <textarea
                        placeholder="Full doorstep address (house no, street, landmark, city)"
                        required
                        rows="3"
                        data-testid="book-location"
                        value={state.location}
                        onChange={(e) => setState({ ...state, location: e.target.value })}
                        className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all resize-none"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-white/90 block mb-1.5">Special Requests (Optional)</label>
                    <textarea
                      rows="2"
                      placeholder="Any specific requests or preferences..."
                      data-testid="book-notes"
                      value={state.notes}
                      onChange={(e) => setState({ ...state, notes: e.target.value })}
                      className="w-full bg-black/70 border border-white/20 focus:border-emerald-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP: Confirm Request (Doorstep Flow) */}
            {activeStepName === "Confirm Request" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Review & Submit Doorstep Request</h2>
                <div className="rounded-2xl border-2 border-emerald-400/80 p-6 bg-black/70 shadow-lg shadow-emerald-950/30 mb-6 space-y-3.5 text-sm">
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Service Mode</span>
                    <span className="text-emerald-400 font-extrabold uppercase tracking-widest text-xs">Doorstep Session Request</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Date & Time</span>
                    <span className="text-white font-bold">{state.date} · {fmt12(state.time)}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Requested Therapist</span>
                    <span className="text-white font-bold">{therapists.find(t => t.id === state.therapist_id)?.display_name || "Any Therapist (No preference)"}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Doorstep Address</span>
                    <span className="text-white font-bold text-right max-w-xs">{state.location}</span>
                  </div>
                  <div className="pt-2 flex items-center justify-between text-amber-300 text-xs font-bold bg-amber-950/30 p-3 rounded-xl border border-amber-400/30">
                    <span>ℹ️ Pricing & Payment:</span>
                    <span>No advance required / Finalized by Branch</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-emerald-400/40 bg-emerald-950/20 text-xs text-white/80 leading-relaxed font-medium mb-6">
                  📞 After submitting, our branch manager will connect with you on <strong className="text-emerald-300">{state.customer_phone}</strong> to confirm therapist availability & session details.
                </div>

                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="book-submit-doorstep-btn"
                  className="gold-btn w-full justify-center rounded-xl py-4 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
                >
                  {busy ? "Submitting Request..." : "Submit Doorstep Appointment Request"}
                </button>
              </div>
            )}

            {/* STEP: Payment (In-Spa Flow) */}
            {activeStepName === "Payment" && (
              <div>
                <h2 className="font-heading text-3xl text-white font-bold mb-6">Review & Confirm Booking</h2>
                <div className="rounded-2xl border-2 border-emerald-400/80 p-6 bg-black/70 shadow-lg shadow-emerald-950/30 mb-6 space-y-3.5 text-sm">
                  {state.booking_type === "spa" && (
                    <div className="flex justify-between border-b border-white/10 pb-2.5">
                      <span className="text-white/60 font-medium">Branch Location</span>
                      <span className="text-white font-bold">{branches.find(b => b.id === state.branch_id)?.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Service Type</span>
                    <span className="text-emerald-400 font-extrabold uppercase tracking-widest text-xs">{state.booking_type}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Date & Time</span>
                    <span className="text-white font-bold">{state.date} · {fmt12(state.time)}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">{selectedPkg ? "Selected Package" : "Selected Service"}</span>
                    <span className="text-amber-400 font-bold">{selectedPkg?.name || selectedSvc?.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2.5">
                    <span className="text-white/60 font-medium">Assigned Therapist</span>
                    <span className="text-white font-bold">{therapists.find(t => t.id === state.therapist_id)?.display_name || "Any Therapist (No preference)"}</span>
                  </div>
                  <div className="pt-2 flex justify-between text-xl font-bold">
                    <span className="text-white">Total Amount</span>
                    <span className="text-amber-400 font-heading">{inr(amount)}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-semibold text-white/90 block mb-3">Choose Payment Mode</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Pay Later */}
                    <button
                      type="button"
                      onClick={() => setPaymentMode("pay_at_counter")}
                      className={`rounded-2xl border-2 p-4 text-left transition-all w-full shadow-md ${
                        paymentMode === "pay_at_counter"
                          ? "border-emerald-400 bg-emerald-950/30 text-white"
                          : "border-white/15 bg-black/40 text-white/70 hover:border-white/30"
                      }`}
                    >
                      <div className="font-heading text-sm font-bold flex justify-between items-center">
                        <span>Pay at Counter</span>
                        {paymentMode === "pay_at_counter" && <Check size={16} className="text-emerald-400" />}
                      </div>
                      <div className="text-xs text-white/70 mt-1 font-normal">Pay with Cash/UPI/Card at spa or doorstep.</div>
                    </button>

                    {/* Pay with Wallet */}
                    <button
                      type="button"
                      disabled={!user || walletBalance < amount}
                      onClick={() => setPaymentMode("wallet")}
                      className={`rounded-2xl border-2 p-4 text-left transition-all w-full shadow-md ${
                        paymentMode === "wallet"
                          ? "border-amber-400 bg-amber-950/30 text-white"
                          : !user || walletBalance < amount
                          ? "border-white/10 bg-black/40 opacity-50 cursor-not-allowed text-white/40"
                          : "border-white/15 bg-black/40 text-white/70 hover:border-amber-400/50"
                      }`}
                    >
                      <div className="font-heading text-sm font-bold flex justify-between items-center">
                        <span>Pay with Wallet Balance</span>
                        {paymentMode === "wallet" && <Check size={16} className="text-amber-400" />}
                      </div>
                      <div className="text-xs text-amber-300 font-bold mt-1">
                        Available: {inr(walletBalance)}
                      </div>
                      {walletBalance < amount && (
                        <div className="text-[10px] text-red-400 font-semibold mt-0.5">
                          {user ? "Insufficient wallet balance" : "Login to use wallet"}
                        </div>
                      )}
                    </button>

                    {/* Pay Online */}
                    <button
                      type="button"
                      disabled
                      className="rounded-2xl border border-white/10 p-4 text-left bg-black/40 opacity-40 cursor-not-allowed w-full"
                    >
                      <div className="font-heading text-sm text-white/50 font-bold flex justify-between items-center">
                        <span>Pay Online</span>
                        <span className="text-[9px] bg-amber-950/80 border border-amber-400/40 text-amber-400 px-1.5 py-0.5 rounded font-extrabold">SOON</span>
                      </div>
                      <div className="text-xs text-white/30 mt-1">Online payment gateway under verification.</div>
                    </button>
                  </div>
                </div>

                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="book-pay-btn"
                  className="gold-btn w-full justify-center rounded-xl py-4 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
                >
                  {busy ? "Processing Booking..." : paymentMode === "wallet" ? `Confirm & Pay ${inr(amount)} via Wallet` : `Confirm Booking (Pay at Counter)`}
                </button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Navigation Buttons (Back / Continue) */}
      <div className="flex justify-between mt-8">
        <button
          disabled={step === 0}
          onClick={() => setStep(Math.max(0, step - 1))}
          data-testid="book-back-btn"
          className="ghost-btn disabled:opacity-30 disabled:cursor-not-allowed rounded-xl px-6 py-3 text-xs uppercase tracking-wider font-extrabold"
        >
          Back
        </button>

        {step < steps.length - 1 && (
          <button
            disabled={!canNext}
            onClick={() => setStep(step + 1)}
            data-testid="book-next-btn"
            className="gold-btn disabled:opacity-30 disabled:cursor-not-allowed rounded-xl px-8 py-3 text-xs uppercase tracking-wider font-extrabold flex items-center gap-2"
          >
            <span>Continue</span>
            <ChevronRight size={14}/>
          </button>
        )}
      </div>
    </div>
  );
}
